import React, { useEffect } from 'react'
import {
  Controller,
  FieldErrorsImpl,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { Button, Checkbox, Group, Stack, Text, TextInput } from '@mantine/core'
import get from 'lodash/get'

import { SelectInput } from '@/components/common/SelectInput'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { SubtleButton } from '@/components/Event/NewScript/components/common/Layout/SubtleButton'
import { ROLES_WITH_ID } from '@/components/Event/NewScript/constants'
import { getFieldName } from '@/components/Event/NewScript/utils/attendees'
import {
  IDENTITY_DOCUMENT_STATUS_OPTIONS,
  IDENTITY_DOCUMENT_TYPE_OPTIONS,
  IdentityDocumentStatus,
  PHOTO_ID_VERIFIED_LABEL,
} from '@/constants'
import { ScriptAttendee, ScriptIdentityDocument } from '@/types'

type Props = {
  attendee: ScriptAttendee
  onSubmitClick?: (attendee: ScriptAttendee) => void
  onCancelClick?: () => void
  hideButton?: boolean
  onValidationStateChange?: (isValid: boolean) => void
  requiredDocumentNumber?: boolean
  namePrefix: string
}

export const IdentityDocumentInput: React.FC<Props> = ({
  attendee,
  onSubmitClick,
  onCancelClick,
  hideButton,
  requiredDocumentNumber = false,
  namePrefix,
}) => {
  const {
    register,
    control,
    clearErrors,
    getValues,
    trigger,
    formState,
    setValue,
  } = useFormContext()
  const usesPhotoIdVerification = ROLES_WITH_ID.includes(attendee.role)
  const identityDocument = useWatch({
    control: control,
    name: namePrefix,
  })

  const isStatusNotPresentOrStipulate = [
    IdentityDocumentStatus.NOT_PRESENT,
    IdentityDocumentStatus.STIPULATE,
  ].includes(identityDocument?.status)

  useEffect(() => {
    if (isStatusNotPresentOrStipulate) {
      clearErrors([
        getFieldName('document_type', namePrefix),
        getFieldName('number', namePrefix),
        getFieldName('photo_id_verified', namePrefix),
      ])
    }
  }, [clearErrors, isStatusNotPresentOrStipulate, namePrefix])

  useEffect(() => {
    if (usesPhotoIdVerification && isStatusNotPresentOrStipulate) {
      if (identityDocument?.document_type !== null) {
        setValue(getFieldName('document_type', namePrefix), null, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }

      if (identityDocument?.photo_id_verified !== false) {
        setValue(getFieldName('photo_id_verified', namePrefix), false, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    }
  }, [
    identityDocument?.document_type,
    identityDocument?.photo_id_verified,
    isStatusNotPresentOrStipulate,
    usesPhotoIdVerification,
    namePrefix,
    setValue,
  ])

  const errors = get(
    formState.errors,
    namePrefix,
    {},
  ) as FieldErrorsImpl<ScriptIdentityDocument>

  const onSaveChangesClick = () => {
    trigger(namePrefix).then(isValid => {
      if (isValid) {
        onSubmitClick?.({
          ...attendee,
          identity_document: {
            ...getValues(namePrefix),
          },
        })
      }
    })
  }

  return (
    <Stack align="flex-start">
      <Stack>
        <Group grow>
          <SelectInput
            control={control}
            name={getFieldName('status', namePrefix)}
            placeholder="Select option"
            label="ID status"
            error={!!errors?.status?.message}
            data={IDENTITY_DOCUMENT_STATUS_OPTIONS}
            withAsterisk
          />
          <SelectInput
            control={control}
            name={getFieldName('document_type', namePrefix)}
            disabled={isStatusNotPresentOrStipulate}
            placeholder="Select option"
            label="ID type"
            error={!!errors?.document_type?.message}
            data={IDENTITY_DOCUMENT_TYPE_OPTIONS}
            withAsterisk={!isStatusNotPresentOrStipulate}
          />
          {!usesPhotoIdVerification && (
            <TextInput
              {...register(getFieldName('number', namePrefix))}
              label="ID number"
              disabled={isStatusNotPresentOrStipulate}
              placeholder="Enter ID number"
              error={!!errors?.number?.message}
              withAsterisk={requiredDocumentNumber}
            />
          )}
        </Group>
        {usesPhotoIdVerification && (
          <Stack spacing="xs">
            <Controller
              control={control}
              name={getFieldName('photo_id_verified', namePrefix)}
              render={({ field }) => {
                const witnessVerificationError = get(
                  errors,
                  'photo_id_verified.message',
                )
                const hasWitnessVerificationError =
                  Boolean(witnessVerificationError) &&
                  !isStatusNotPresentOrStipulate

                return (
                  <Checkbox
                    label={
                      <span>
                        <span style={{ color: '#212529' }}>
                          {PHOTO_ID_VERIFIED_LABEL}
                        </span>
                        {!isStatusNotPresentOrStipulate && (
                          <span aria-hidden="true" style={{ color: '#fa5252' }}>
                            {' '}
                            *
                          </span>
                        )}
                      </span>
                    }
                    disabled={isStatusNotPresentOrStipulate}
                    checked={!!field.value}
                    onChange={event =>
                      field.onChange(event.currentTarget.checked)
                    }
                    error={hasWitnessVerificationError}
                    styles={{
                      label: hasWitnessVerificationError
                        ? {
                            color: '#fa5252',
                          }
                        : undefined,
                      input: hasWitnessVerificationError
                        ? {
                            borderColor: '#fa5252',
                          }
                        : undefined,
                    }}
                  />
                )
              }}
            />
            <Text color="gray.7" size="sm">
              General Note: ID numbers are not required for our internal
              records. If your specific state or regional regulations require
              you to log ID details in your physical notary journal, you may
              continue to do so at your discretion.
            </Text>
          </Stack>
        )}
        <FormErrors<ScriptIdentityDocument> errors={errors} />
      </Stack>
      {!hideButton && (
        <Group>
          <Button
            variant="filled"
            color="primary.2"
            onClick={onSaveChangesClick}
          >
            Save changes
          </Button>
          <SubtleButton onClick={onCancelClick}>Cancel</SubtleButton>
        </Group>
      )}
    </Stack>
  )
}
